import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Wifi, Plug, MapPin } from "lucide-react";
import Navbar from "@/components/Navbar";
// Footer removed — single-screen layout on desktop
import { Skeleton } from "@/components/ui/skeleton";

interface CollectionSpot {
  name: string;
  short_address: string;
  category: string;
  rating: number | null;
  cover_photo: string | null;
  wifi_available: boolean;
  power_outlets: boolean;
}

interface Collection {
  id: string;
  name: string;
  description: string | null;
  spot_count: number;
  spots: CollectionSpot[];
}

const categoryLabels: Record<string, string> = {
  cafe: "Café",
  gym: "Gym",
  hotel_lobby: "Hotel Lobby",
  coworking: "Coworking",
  library: "Library",
  restaurant: "Restaurant",
  other: "Other",
};

const SpotCard = ({ spot, index }: { spot: CollectionSpot; index: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: index * 0.1 }}
    className="bg-card rounded-xl overflow-hidden border border-border shadow-sm flex flex-col"
  >
    <div className="aspect-[16/9] bg-muted relative overflow-hidden flex-shrink-0">
      {spot.cover_photo ? (
        <img
          src={spot.cover_photo}
          alt={spot.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <MapPin className="w-6 h-6 text-muted-foreground/40" />
        </div>
      )}
      <span className="absolute top-2 left-2 bg-background/90 backdrop-blur-sm text-foreground text-[11px] font-medium px-2 py-0.5 rounded-full">
        {categoryLabels[spot.category] || spot.category}
      </span>
    </div>
    <div className="p-3 flex-1">
      <h3 className="font-semibold text-foreground text-sm leading-snug mb-0.5 truncate">
        {spot.name}
      </h3>
      {spot.short_address && (
        <p className="text-xs text-muted-foreground mb-1.5 truncate">{spot.short_address}</p>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {spot.rating != null && (
          <div className="flex items-center gap-1 text-xs text-foreground/80">
            <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
            <span>{spot.rating.toFixed(1)}</span>
          </div>
        )}
        {spot.wifi_available && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Wifi className="w-3 h-3" />
            <span>WiFi</span>
          </div>
        )}
        {spot.power_outlets && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Plug className="w-3 h-3" />
            <span>Power</span>
          </div>
        )}
      </div>
    </div>
  </motion.div>
);


const CardSkeleton = () => (
  <div className="bg-card rounded-2xl overflow-hidden border border-border">
    <Skeleton className="aspect-[16/10] w-full" />
    <div className="p-4 space-y-2">
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-3 w-1/2" />
      <Skeleton className="h-3 w-1/3" />
    </div>
  </div>
);

const CollectionView = () => {
  const { id } = useParams<{ id: string }>();
  const [collection, setCollection] = useState<Collection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<"not_found" | "error" | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);

    fetch(`https://spotsvc.up.railway.app/collections/${id}`)
      .then((res) => {
        if (res.status === 404) throw new Error("not_found");
        if (!res.ok) throw new Error("error");
        return res.json();
      })
      .then((data) => setCollection(data))
      .catch((err) =>
        setError(err.message === "not_found" ? "not_found" : "error")
      )
      .finally(() => setLoading(false));
  }, [id]);

  const allSpots = collection?.spots ?? [];

  return (
    <div className="min-h-screen md:h-screen md:overflow-hidden bg-background flex flex-col">
      <Navbar />

      <main className="flex-1 max-w-7xl w-full mx-auto px-6 py-6 md:py-4 flex flex-col min-h-0">
        {/* Loading */}
        {loading && (
          <div className="space-y-4">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-5 w-1/4" />
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_300px] gap-4 mt-4">
              <div className="grid grid-cols-1 gap-3">
                <CardSkeleton />
                <CardSkeleton />
                <CardSkeleton />
                <CardSkeleton />
              </div>
              <CardSkeleton />
            </div>
          </div>
        )}

        {/* Not found */}
        {error === "not_found" && (
          <div className="text-center py-20">
            <h1 className="font-funnel text-2xl font-bold text-foreground mb-2">
              Collection not found
            </h1>
            <p className="text-muted-foreground">
              This collection doesn't exist or hasn't been shared.
            </p>
          </div>
        )}

        {/* Generic error */}
        {error === "error" && (
          <div className="text-center py-20">
            <h1 className="font-funnel text-2xl font-bold text-foreground mb-2">
              Something went wrong
            </h1>
            <p className="text-muted-foreground">
              We couldn't load this collection. Please try again later.
            </p>
          </div>
        )}

        {/* Collection loaded */}
        {!loading && !error && collection && (
          <div className="flex flex-col flex-1 min-h-0">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mb-4"
            >
              <h1 className="font-funnel text-2xl sm:text-3xl font-bold text-foreground">
                {collection.name}
              </h1>
              {collection.description && (
                <p className="text-sm text-muted-foreground mt-1">{collection.description}</p>
              )}
            </motion.div>

            {/* CTA banner */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.15 }}
              className="rounded-2xl bg-primary px-6 py-4 flex items-center justify-between mb-4"
            >
              <div>
              <h2 className="font-funnel text-base font-bold text-primary-foreground">
                  Create your own collections
                </h2>
                <p className="text-primary-foreground/70 text-sm">
                  Download the qwip app to save spots and build collections like this one.
                </p>
              </div>
              <a
                href="https://apps.apple.com/app/qwip"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 ml-4"
              >
                <img
                  src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                  alt="Download on the App Store"
                  className="h-10"
                />
              </a>
            </motion.div>

            {/* Spot grid */}
            <div className="grid grid-cols-2 gap-3 flex-1 min-h-0 auto-rows-fr overflow-y-auto">
              {allSpots.map((spot, i) => (
                <SpotCard key={i} spot={spot} index={i} />
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CollectionView;
